/**
 * When is an element in a state? (STATES_AND_INTERACTIVE_ELEMENTS_PLAN §13.4)
 *
 * `stateWhen` on an element lists the states a condition switches on:
 * Disabled (→ `aria-disabled="true"`) and custom states (→ `data-pb-state`).
 * One model for every renderer and the page runtime; framework-free, and
 * storefront copies are hand-mirrored.
 *
 * Two kinds of condition:
 * - decided on the server, from data the page already has: a page / post
 *   field, a card item's field (In stock);
 * - live, re-checked in the browser by the page runtime because cached HTML
 *   would freeze them: store hours, the visitor's store, a time window.
 *
 * Store conditions are NOT met until the visitor has a store (John,
 * 2026-10-12: multi-store sites make you choose one before anything can be
 * bought; a one-store site always has its store). Hours that aren't entered
 * are not met either: a missing hour never disables a button.
 */
import { storeStatus, type StoreSchedule } from './store-status';

export type StoreCondition =
  | { source: 'store'; is: 'open' | 'closed' }
  | { source: 'store'; is: 'opens-within' | 'closes-within'; minutes: number }
  | { source: 'store'; is: 'chosen' | 'not-chosen' }
  | { source: 'store'; is: 'is' | 'is-not'; storeId: string }
  /** Several stores at once (the Slides window, 2026-10-21): shown at any of them, or at none of them. */
  | { source: 'store'; is: 'one-of' | 'not-one-of'; storeIds: string[] };

export interface TimeCondition {
  source: 'time';
  /** 0 = Sunday … 6 = Saturday; none = every day. */
  days?: number[];
  /** `HH:MM`, the site's timezone; `to` before `from` runs past midnight. */
  from?: string;
  to?: string;
  /**
   * A date window, as ISO instants (SCHEDULING_PLAN.md §2.3): met from
   * `fromDate` (inclusive) until `untilDate` (exclusive). Either may be
   * absent. With no days and no times, the window alone decides — "just
   * once"; with them, the weekly pattern runs inside the window — "repeating".
   */
  fromDate?: string;
  untilDate?: string;
}

/**
 * The one state with a reserved meaning: an element carrying a `visible`
 * rule is hidden unless the rule is met. The renderers write `hidden` (and
 * `data-pb-hidden`, which the baked CSS backs with `display: none`, since an
 * author's `display` would otherwise beat the attribute); the page runtime
 * adds and removes them on the minute. The editing canvas never hides —
 * it marks (SCHEDULING_PLAN.md §3.2).
 */
export const VISIBLE_STATE = 'visible';

export interface FieldCondition {
  source: 'field';
  /** A binding key: `page:…` (the page / post) or an item field in a card. */
  field: string;
  is: 'empty' | 'not-empty' | 'equals' | 'not-equals';
  value?: string;
}

/**
 * A condition already decided on the server. Only ever written by
 * `liveRulesFor`, so the page runtime can evaluate a rule that mixes a field
 * condition (which it cannot see) with a live one.
 */
export interface DecidedCondition {
  source: 'decided';
  value: boolean;
}

/**
 * WHO THE VISITOR IS (PERSONALISATION_PLAN.md §15). One grammar for every
 * audience, read from `id`:
 *   `everyone` · `signed-out` · `signed-in` · `club` (the site's loyalty
 *   group) · `tier:<name>` (a loyalty tier, by its label) · `segment:<id>`
 *   (a Loyalty › Segments row, by its id).
 * Decided on the server from `ConditionContext.visitor` — never live: a
 * visitor does not change mid-page, and a page that depends on who is looking
 * is rendered for them, not cached for everyone. A storefront that does not
 * know its visitor yet decides every audience as signed out, which is exactly
 * the page it serves today.
 */
export interface AudienceCondition {
  source: 'audience';
  id: string;
  is: 'in' | 'not-in';
  /**
   * Stamped by the save when it fans an Automatic copy out into one copy per
   * audience variant (expand-instances.ts), never written by a designer. The
   * editor strips stamped conditions when a tree comes back to it.
   */
  auto?: true;
}

/** What the audience conditions read about the visitor. */
export interface VisitorAudience {
  signedIn: boolean;
  /** In the site's loyalty group (Embarc: Passport Club). */
  club: boolean;
  /** Their loyalty tier's label, or null. */
  tier: string | null;
  /** The Loyalty › Segments rows they are in, by id. */
  segmentIds: readonly string[];
}

/** The visitor a page is rendered for when nobody is known: a guest. */
export const SIGNED_OUT_VISITOR: VisitorAudience = { signedIn: false, club: false, tier: null, segmentIds: [] };

/** Is this visitor in the audience `id` names? An id nobody wrote is nobody's. */
export function inAudience(id: string, visitor: VisitorAudience): boolean {
  if (id === 'everyone') return true;
  if (id === 'signed-out') return !visitor.signedIn;
  if (id === 'signed-in') return visitor.signedIn;
  if (id === 'club') return visitor.signedIn && visitor.club;
  if (id.startsWith('tier:')) return visitor.signedIn && !!visitor.tier && visitor.tier.trim().toLowerCase() === id.slice(5).trim().toLowerCase();
  if (id.startsWith('segment:')) return visitor.signedIn && visitor.segmentIds.includes(id.slice(8));
  return false;
}

export type Condition = StoreCondition | TimeCondition | FieldCondition | AudienceCondition | DecidedCondition;

export interface StateWhen {
  /** `disabled`, or a custom state's name. */
  state: string;
  when: Condition[];
  /** All conditions (default) or any of them. */
  match?: 'all' | 'any';
}

export interface ActiveStore {
  id: string;
  schedule?: StoreSchedule | null;
}

export interface ConditionContext {
  now: number;
  /** The visitor's store; null when none is chosen yet. */
  store?: ActiveStore | null;
  /** The site's timezone, for time windows and stores without one. */
  timezone?: string;
  /** A field's value; server-side only. */
  field?: (key: string) => unknown;
  /** Who is looking; absent or null = a guest (`SIGNED_OUT_VISITOR`). */
  visitor?: VisitorAudience | null;
}

export function isLiveCondition(c: Condition): boolean {
  return c.source === 'store' || c.source === 'time';
}

/** True when a `stateWhen` must be re-checked in the browser. */
export function isLiveStateWhen(rule: StateWhen): boolean {
  return rule.when.some(isLiveCondition);
}

function minutesOf(t: string | undefined): number | null {
  const m = t ? /^(\d{1,2}):(\d{2})/.exec(t) : null;
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

function localClock(now: number, timeZone: string): { dow: number; minutes: number } {
  const fmt = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(new Date(now))) p[part.type] = part.value;
  return { dow: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(p.weekday), minutes: (+p.hour % 24) * 60 + +p.minute };
}

function evaluateStore(c: StoreCondition, ctx: ConditionContext): boolean {
  const store = ctx.store ?? null;
  if (c.is === 'chosen') return store !== null;
  if (c.is === 'not-chosen') return store === null;
  if (!store) return false;
  if (c.is === 'is') return store.id === c.storeId;
  if (c.is === 'is-not') return store.id !== c.storeId;
  if (c.is === 'one-of') return c.storeIds.includes(store.id);
  if (c.is === 'not-one-of') return !c.storeIds.includes(store.id);
  if (!store.schedule) return false;
  const s = storeStatus(store.schedule, ctx.now, { fallbackTimezone: ctx.timezone });
  switch (c.is) {
    case 'open':
      return s.state === 'open';
    case 'closed':
      return s.state === 'closed';
    case 'opens-within':
      return s.state === 'closed' && s.minutesToOpen !== undefined && s.minutesToOpen <= c.minutes;
    case 'closes-within':
      return s.state === 'open' && s.minutesToClose !== undefined && s.minutesToClose <= c.minutes;
  }
}

function evaluateTime(c: TimeCondition, ctx: ConditionContext): boolean {
  // The date window first: outside it nothing else matters.
  if (c.fromDate) {
    const f = Date.parse(c.fromDate);
    if (!Number.isNaN(f) && ctx.now < f) return false;
  }
  if (c.untilDate) {
    const u = Date.parse(c.untilDate);
    if (!Number.isNaN(u) && ctx.now >= u) return false;
  }
  const { dow, minutes } = localClock(ctx.now, ctx.timezone || 'UTC');
  const from = minutesOf(c.from);
  const to = minutesOf(c.to);
  if (from === null || to === null) return !c.days || c.days.includes(dow);
  if (from <= to) return (!c.days || c.days.includes(dow)) && minutes >= from && minutes < to;
  // Past midnight: before `to` belongs to the window that started yesterday.
  if (minutes >= from) return !c.days || c.days.includes(dow);
  if (minutes < to) return !c.days || c.days.includes((dow + 6) % 7);
  return false;
}

function isEmpty(v: unknown): boolean {
  return v === undefined || v === null || (typeof v === 'string' && v.trim() === '') || (Array.isArray(v) && v.length === 0);
}

function evaluateField(c: FieldCondition, ctx: ConditionContext): boolean {
  if (!ctx.field) return false;
  const v = ctx.field(c.field);
  switch (c.is) {
    case 'empty':
      return isEmpty(v);
    case 'not-empty':
      return !isEmpty(v);
    case 'equals':
      return String(v ?? '').trim().toLowerCase() === String(c.value ?? '').trim().toLowerCase();
    case 'not-equals':
      return String(v ?? '').trim().toLowerCase() !== String(c.value ?? '').trim().toLowerCase();
  }
}

function evaluateAudience(c: AudienceCondition, ctx: ConditionContext): boolean {
  const is = inAudience(c.id, ctx.visitor ?? SIGNED_OUT_VISITOR);
  return c.is === 'not-in' ? !is : is;
}

export function evaluateCondition(c: Condition, ctx: ConditionContext): boolean {
  if (c.source === 'decided') return c.value;
  if (c.source === 'store') return evaluateStore(c, ctx);
  if (c.source === 'time') return evaluateTime(c, ctx);
  if (c.source === 'audience') return evaluateAudience(c, ctx);
  return evaluateField(c, ctx);
}

/**
 * A `visible` rule the server has fully decided against: nothing live in it
 * and not met. The renderers leave such an element OUT of the HTML rather
 * than writing `hidden` — a member price must never reach a guest's page,
 * even hidden (PERSONALISATION_PLAN.md §15). A rule with a live condition is
 * still written and hidden, for the page runtime to re-check.
 */
export function omittedByRules(rules: readonly StateWhen[], ctx: ConditionContext): boolean {
  const visible = rules.filter((r) => r.state === VISIBLE_STATE);
  if (visible.length === 0 || visible.some(isLiveStateWhen)) return false;
  return !visible.some((r) => evaluateStateWhen(r, ctx));
}

export function evaluateStateWhen(rule: StateWhen, ctx: ConditionContext): boolean {
  if (rule.when.length === 0) return false;
  return rule.match === 'any'
    ? rule.when.some((c) => evaluateCondition(c, ctx))
    : rule.when.every((c) => evaluateCondition(c, ctx));
}

/**
 * The attributes a set of rules gives an element: `aria-disabled` for
 * Disabled, `data-pb-state` (space-separated, matched with `~=`) for the
 * rest. Off states are omitted, never written as "false".
 */
export function stateAttributes(rules: readonly StateWhen[], ctx: ConditionContext): Record<string, string> {
  const on = rules.filter((r) => evaluateStateWhen(r, ctx)).map((r) => r.state);
  const attrs: Record<string, string> = {};
  if (on.includes('disabled')) attrs['aria-disabled'] = 'true';
  // `visible` is governed here and absent from the list: an element with a
  // visible rule that is NOT met is hidden; one whose rule holds carries nothing.
  if (rules.some((r) => r.state === VISIBLE_STATE) && !on.includes(VISIBLE_STATE)) {
    attrs.hidden = '';
    attrs['data-pb-hidden'] = '';
  }
  const custom = [...new Set(on.filter((s) => s !== 'disabled' && s !== VISIBLE_STATE))];
  if (custom.length) attrs['data-pb-state'] = custom.join(' ');
  return attrs;
}

/**
 * The rules the page runtime must keep re-checking, with every condition the
 * server can decide already decided (`{ source: 'decided' }`). Rules with no
 * live condition are left out: their answer is in the HTML for good. Written
 * to the element as `data-pb-when` (JSON).
 */
export function liveRulesFor(rules: readonly StateWhen[], ctx: ConditionContext): StateWhen[] {
  return rules
    .filter(isLiveStateWhen)
    .map((rule) => ({
      ...rule,
      when: rule.when.map((c): Condition => (isLiveCondition(c) ? c : { source: 'decided', value: evaluateCondition(c, ctx) })),
    }));
}

/**
 * Every attribute a conditional element carries in server HTML: the states
 * on right now, plus `data-pb-when` when anything must be re-checked live.
 * One call for all three renderers.
 */
export function conditionalStateAttributes(rules: readonly StateWhen[] | undefined, ctx: ConditionContext): Record<string, string> {
  if (!rules || rules.length === 0) return {};
  const attrs = stateAttributes(rules, ctx);
  const live = liveRulesFor(rules, ctx);
  if (live.length) attrs['data-pb-when'] = JSON.stringify(live);
  return attrs;
}

/** A saved `stateWhen` value read defensively: anything malformed is dropped. */
export function readStateWhen(value: unknown): StateWhen[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (r): r is StateWhen =>
      !!r && typeof r === 'object' && typeof (r as StateWhen).state === 'string' && /^[a-z][a-z0-9-]{0,31}$/.test((r as StateWhen).state) && Array.isArray((r as StateWhen).when),
  );
}

